import { Fragment, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Divider } from 'ui-components/components/divider';
import { DropdownMenu } from 'ui-components/components/dropdown-menu/';
import { DropdownMenuType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message/types';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import * as availablePresetOptions from './constants';
import style from './edit-preset.module.css';
import { messages } from './messages';
import { presetOptionComponents } from './preset-option-renderers';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { useBarcodeScanningFeatureGate } from '../../feature-gates/use-barcode-scanning-feature-gate';
import { ShippingPresetPropType } from '../../prop-types/shipping-preset';
import { presetService } from '../../services/preset-service';
import Global from '../../store/global-selectors';

const HOTKEY_FIELD_ID = 'edit-preset-hotkey';

export const EditPreset = ({ preset = emptyObject, updatePreset, validation = emptyObject, isEdit }) => {
  const intl = useIntl();
  const { isFeatureEnabled, disabledMessage } = useBarcodeScanningFeatureGate();

  const serviceInfo = useSelector((state) =>
    Global.selectSellerServiceById(state, {
      serviceId: preset?.presetOptions?.shippingService?.serviceId,
    }),
  );
  const presets = useSelector(Global.selectSellerPresets) || emptyArray;
  const carriers = useSelector(Global.selectReferenceDatagetSellerCarriers);
  const [selectedService, setSelectedService] = useState();
  const { presetOptions } = preset;

  const selectedOptions = useMemo(() => {
    return Object.entries(presetOptions).reduce((acc, [key, v]) => (v === null ? acc : [...acc, key]), []); // options selected for the preset configuration will have a not null value.
  }, [presetOptions]);

  const onPresetChangeForInput = useCallback(
    (e) => {
      const {
        target: { name, value },
      } = e;
      updatePreset((p) => ({
        ...p,
        [name]: value,
      }));
    },
    [updatePreset],
  );

  const onPresetChange = useCallback(
    ({ name, value }) => {
      updatePreset((p) => ({
        ...p,
        [name]: value,
      }));
    },
    [updatePreset],
  );

  const onPresetOptionChange = useCallback(
    ({ name, value, carrierId = '' }) => {
      updatePreset((p) => ({
        ...p,
        presetOptions: {
          ...p?.presetOptions,
          carrierId,
          [name]: value,
        },
      }));
    },
    [updatePreset],
  );

  const selectOption = useCallback(
    (name) => {
      // setting the value for the option to an explicit undefined lets us know the user hasn't touched that option, and we can strip it from the request when the user saves their configuration.
      onPresetOptionChange({ name, value: undefined });
    },
    [onPresetOptionChange],
  );

  const removeOption = useCallback(
    (name) => {
      const shouldRemoveConfirmationOption = name === availablePresetOptions.shippingService;
      if (shouldRemoveConfirmationOption) {
        onPresetOptionChange({
          name: availablePresetOptions.confirmationId,
          value: null,
        });
      }

      onPresetOptionChange({ name, value: null });
    },
    [onPresetOptionChange],
  );

  const confirmationTypes = useMemo(() => presetService.getConfirmationTypes(serviceInfo, carriers) || [], [carriers, serviceInfo]);

  return (
    <div className={style['edit-preset-body']}>
      <FlexColumn gap={Gap.Space8}>
        {!isEdit && <Text textType={TextType.Paragraph} value={messages.newPreset} />}
        <div className={style['name-hotkey-row']}>
          <FlexColumn className={style['preset-name-field']}>
            <Text textType={TextType.Label} value={messages.name} />
            <Input
              inlineMessage={validation?.name ? <FormattedMessage {...messages[validation.name]} /> : null}
              inlineMessageType={INLINE_MESSAGE_TYPES.error}
              name='name'
              onChange={onPresetChangeForInput}
              title={intl.formatMessage(messages.name)}
              value={preset?.name || ''}
              version={version2}
            />
          </FlexColumn>
          <FlexColumn className={style['hotkey-field']}>
            <Text htmlFor={HOTKEY_FIELD_ID} textType={TextType.Label} value={messages.hotkey} />
            <DropdownMenu
              disabled={!isFeatureEnabled}
              disabledTooltipMessage={disabledMessage ? <FormattedMessage {...disabledMessage} /> : null}
              dropdownMenuShouldHaveWidthOfButton
              dropdownMenuType={DropdownMenuType.ComboBox}
              fullWidth
              id={HOTKEY_FIELD_ID}
              variant='form'
            >
              <DropdownMenuToggler className={style['hotkey-dropdown-toggler']}>
                {preset?.hotKey ? `${presetService.generatePresetDropdownEntry(preset.hotKey).display}` : intl.formatMessage(messages.none)}
              </DropdownMenuToggler>
              <DropdownMenuItem
                key='none'
                onClick={() => {
                  onPresetChange({ name: 'hotKey', value: undefined });
                }}
              >
                {intl.formatMessage(messages.none)}
              </DropdownMenuItem>
              {presetService.getDropdownPresetHotkeys().map((hotkey) => (
                <DropdownMenuItem
                  key={hotkey.value}
                  disabled={presets.some((p) => p.hotKey === hotkey.value)}
                  onClick={() => {
                    onPresetChange({ name: 'hotKey', value: hotkey.value });
                  }}
                >
                  {hotkey.key ? `${hotkey.display}` : ''}
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </FlexColumn>
        </div>
      </FlexColumn>
      <FlexColumn gap={Gap.Space8}>
        <Text textType={TextType.H4} value={messages.shippingOptions} />
        <div className={style['available-options-container']}>
          {Object.keys(availablePresetOptions)
            .filter((key) => !selectedOptions.includes(key))
            .map((key, index, array) => (
              <Fragment key={key}>
                <Button
                  className={style['available-options-button']}
                  disabled={!confirmationTypes && key === availablePresetOptions.confirmationId}
                  onClick={() => selectOption(key)}
                  theme={THEME_CONSTANTS.ghost}
                >
                  <FormattedMessage {...messages[key]} />
                </Button>
                {index < array.length - 1 && <Divider className={style['option-divider']} orientation='vertical' />}
              </Fragment>
            ))}
        </div>
        {validation?.presetOptions ? (
          <div className={style['error-message-container']}>
            <BannerMessageV2 content={intl.formatMessage(messages[validation.presetOptions])} status={BannerStatus.Error} />
          </div>
        ) : null}
        {Object.entries(presetOptionComponents)
          .filter(([key]) => selectedOptions.includes(key))
          .map(([key, OptionComponent]) => {
            return (
              <div key={key} className={style['added-preset-container']}>
                <div className={style['added-preset-content']}>
                  <OptionComponent
                    confirmations={confirmationTypes}
                    data={preset?.presetOptions[key]}
                    onChange={onPresetOptionChange}
                    selectedCarrierId={selectedService}
                    selectedPackageCarrierId={preset?.presetOptions?.carrierId}
                    setSelectedService={setSelectedService}
                  />
                </div>
                <Button
                  ariaLabel={intl.formatMessage(messages.removeShippingOptionButtonAriaLabel)}
                  className={style['remove-option-button']}
                  onClick={() => removeOption(key)}
                  testId='remove-option-button'
                  theme={THEME_CONSTANTS.unstyled}
                >
                  <FontAwesomeIcon className={style.icon} icon={faXmark} />
                </Button>
              </div>
            );
          })}
      </FlexColumn>
    </div>
  );
};

EditPreset.propTypes = {
  preset: ShippingPresetPropType,
  updatePreset: PropTypes.func.isRequired,
  validation: PropTypes.shape({
    name: PropTypes.string,
    presetOptions: PropTypes.string,
  }),
  isEdit: PropTypes.bool.isRequired,
};
